import { ApiError, errorMessage } from '../api/client'
import type { Suggestion } from '../api/types'
import { cn } from '../lib/cn'
import { fmtDate, fmtRange } from '../lib/time'
import { Button } from './ui'

/**
 * Показ ошибки.
 *
 * Отдельный случай — slot_taken. Пересечения запрещает база, поэтому второй из двух
 * одновременных запросов всегда получит отказ, и это не сбой, а обычное дело.
 * Показывать здесь голое сообщение — значит отправить человека начинать заново.
 * Вместо этого сразу под текстом идут альтернативы, и одним нажатием он оказывается
 * в той же форме с другим временем.
 */
export function ErrorNotice({
  error,
  onPickSuggestion,
  onRetry,
  className,
}: {
  error: unknown
  onPickSuggestion?: (suggestion: Suggestion) => void
  onRetry?: () => void
  className?: string
}) {
  if (!error) return null

  const isApi = error instanceof ApiError
  const code = isApi ? error.code : undefined
  const suggestions = isApi ? error.suggestions : undefined
  const details = isApi ? error.details : undefined

  return (
    <div
      role="alert"
      className={cn(
        'rounded-card border px-4 py-3',
        code === 'slot_taken'
          ? 'border-state-soon/40 bg-state-soon/10'
          : 'border-state-busy/40 bg-state-busy/10',
        className,
      )}
    >
      <p className="text-sm font-medium text-fg">{title(code)}</p>
      <p className="mt-1 text-sm text-fg/80">{errorMessage(error)}</p>

      {details && Object.keys(details).length > 0 && (
        <ul className="mt-2 list-inside list-disc text-sm text-fg/80">
          {Object.entries(details).map(([field, message]) => (
            <li key={field}>{message}</li>
          ))}
        </ul>
      )}

      {suggestions && suggestions.length > 0 && onPickSuggestion && (
        <SuggestionList suggestions={suggestions} onPick={onPickSuggestion} className="mt-3" />
      )}

      {onRetry && (
        <Button size="sm" variant="ghost" className="mt-3" onClick={onRetry}>
          Повторить
        </Button>
      )}
    </div>
  )
}

function title(code: string | undefined): string {
  switch (code) {
    case 'slot_taken':
      return 'Слот только что заняли'
    case 'policy_violation':
      return 'Так забронировать нельзя'
    case 'blocked':
      return 'Бронирование вам недоступно'
    case 'forbidden':
      return 'Действие недоступно'
    case 'validation_failed':
      return 'Проверьте поля формы'
    case 'not_found':
      return 'Не нашли'
    case 'rate_limited':
      return 'Слишком часто'
    default:
      return 'Не получилось'
  }
}

/** Список альтернатив: показывается и при slot_taken, и когда свободных комнат нет. */
export function SuggestionList({
  suggestions,
  onPick,
  className,
  heading = 'Свободно рядом:',
}: {
  suggestions: Suggestion[]
  onPick: (suggestion: Suggestion) => void
  className?: string
  heading?: string
}) {
  if (suggestions.length === 0) return null
  return (
    <div className={className}>
      <p className="mb-2 text-sm font-medium">{heading}</p>
      <ul className="flex flex-col gap-2">
        {suggestions.map((suggestion, index) => (
          <li key={`${suggestion.room_id}-${suggestion.starts_at}-${index}`}>
            <button
              type="button"
              onClick={() => onPick(suggestion)}
              className="flex w-full items-center justify-between gap-3 rounded-lg border border-line bg-surface px-3 py-2 text-left transition hover:border-accent hover:bg-accent-soft/40"
            >
              <span>
                <span className="block text-sm font-medium">{suggestion.room_name}</span>
                <span className="tnum block text-sm text-muted">
                  {fmtDate(suggestion.starts_at)}, {fmtRange(suggestion.starts_at, suggestion.ends_at)}
                </span>
                {/* Комнату меньше нужной сервер предлагает только когда подходящих
                    нет вовсе. Промолчать об этом здесь — значит вернуть ровно ту
                    беду, от которой пометка и заведена: человек просил место на
                    пятнадцать, а нажимает на комнату для троих. */}
                {suggestion.note && (
                  <span className="mt-0.5 block text-sm text-state-soon">{suggestion.note}</span>
                )}
              </span>
              <span className="text-sm font-medium text-accent">Выбрать</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
